import { inject, Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { Season } from '@gbr-monorepo/interfaces';
import { finalize, Observable } from 'rxjs';
import { SeasonForm, SeasonFormStepper } from '../../models/season-create.model';
import { SeasonCreateOperations } from '../../operations/season-creation.operations';
import { SeasonService } from '../../services/season.service';
import {
    DecrementStepper,
    IncrementStepper,
    ResetSeasonForm,
    ResetStepper,
    SetStepper,
    SubmitForm
} from './season-form.action';

export interface SeasonFormStateModel {
    stepper: SeasonFormStepper;
    seasonCreationForm: {
        model: SeasonForm;
    };
    isSavingSeason: boolean;
}

@State<SeasonFormStateModel>({
    name: 'seasonForm',
    defaults: {
        stepper: {
            active: 0,
            selected: 0
        },
        seasonCreationForm: undefined,
        isSavingSeason: false
    }
})
@Injectable()
export class SeasonFormState {
    private seasonService = inject(SeasonService);

    @Selector()
    static getForm(state: SeasonFormStateModel): SeasonForm {
        return state.seasonCreationForm.model;
    }

    @Selector()
    static getFormStepper(state: SeasonFormStateModel): SeasonFormStepper {
        return state.stepper;
    }

    @Selector()
    static isSavingSeason(state: SeasonFormStateModel): boolean {
        return state.isSavingSeason;
    }

    @Action(SetStepper)
    setStepper(context: StateContext<SeasonFormStateModel>, action: SetStepper) {
        context.patchState({
            stepper: {
                active: action.currentStepIndex,
                selected: action.currentStepIndex
            }
        });
    }

    @Action(SubmitForm)
    submitForm(ctx: StateContext<SeasonFormStateModel>): Observable<Season> {
        const seasonState = ctx.getState();
        const currentSeasonState = seasonState.seasonCreationForm?.model;
        const seasonToBeSubmitted = SeasonCreateOperations.mapSeasonFormToSeasonResponse(currentSeasonState);

        ctx.patchState({ isSavingSeason: true });

        const observable = seasonToBeSubmitted?.id
            ? this.seasonService.updateSeason(seasonToBeSubmitted)
            : this.seasonService.createSeason(seasonToBeSubmitted);

        return observable.pipe(finalize(() => ctx.patchState({ isSavingSeason: false })));
    }

    @Action(IncrementStepper)
    incrementStepper(context: StateContext<SeasonFormStateModel>) {
        const currentStepperValue = context.getState().stepper;
        context.patchState({
            stepper: {
                active: currentStepperValue.active + 1,
                selected: currentStepperValue.selected + 1
            }
        });
    }

    @Action(DecrementStepper)
    decrementStepper(context: StateContext<SeasonFormStateModel>) {
        const currentStepperValue = context.getState().stepper;
        context.patchState({
            stepper: {
                active: currentStepperValue.active - 1,
                selected: currentStepperValue.selected - 1
            }
        });
    }

    @Action(ResetStepper)
    resetStepper(context: StateContext<SeasonFormStateModel>) {
        context.patchState({
            stepper: {
                active: 0,
                selected: 0
            }
        });
    }

    @Action(ResetSeasonForm)
    resetForm(context: StateContext<SeasonFormStateModel>) {
        context.patchState({
            stepper: {
                active: 0,
                selected: 0
            },
            seasonCreationForm: undefined
        });
    }
}
